/**
 * React Helpers for SiteTheme Integration
 * Framework-level React components and hooks
 */

import React from 'react'
import { createThemeInitScript } from './index.js'

/**
 * ThemeProvider component - Wraps your app to provide theme context
 */
export function ThemeProvider({ children, themeData }) {
  React.useEffect(() => {
    // Auto-initialize theme system when provider mounts
    if (typeof window !== 'undefined' && window.SiteTheme) {
      window.SiteTheme.initialize(themeData)
    }
  }, [themeData])

  return <>{children}</>
}

/**
 * ThemeHead component - Renders theme-related head elements
 * Use this in your +Head.jsx file
 */
export function ThemeHead({ data }) {
  const themeData = data?.themeData
  const cmsColorCSS = data?.cmsColorCSS

  return (
    <>
      {/* Theme initialization script */}
      {themeData && (
        <script
          key="theme-init"
          dangerouslySetInnerHTML={{ __html: createThemeInitScript(themeData) }}
        />
      )}
      
      {/* CMS color utilities CSS */}
      {cmsColorCSS?.css && (
        <style
          key="cms-color-utilities"
          id="cms-color-utilities"
          dangerouslySetInnerHTML={{ __html: cmsColorCSS.css }}
        />
      )}
    </>
  )
}

/**
 * useTheme hook - Access current theme data in any component
 */
export function useTheme() {
  const [currentTheme, setCurrentTheme] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.SiteTheme) {
      // Get current theme
      const theme = window.SiteTheme.getTheme()
      setCurrentTheme(theme)
      setIsLoading(false)

      // Listen for theme changes
      const handleThemeChange = (newTheme) => {
        setCurrentTheme(newTheme)
      }

      window.SiteTheme.onThemeChange(handleThemeChange)

      // Cleanup listener
      return () => {
        if (window.SiteTheme.offThemeChange) {
          window.SiteTheme.offThemeChange(handleThemeChange)
        }
      }
    }
  }, [])

  return {
    theme: currentTheme,
    isLoading,
    setTheme: (themeId) => {
      if (typeof window !== 'undefined' && window.SiteTheme) {
        return window.SiteTheme.setTheme(themeId)
      }
    },
    getUserPreferredThemeId: (context = 'site') => {
      if (typeof window !== 'undefined' && window.SiteTheme) {
        return window.SiteTheme.getUserPreferredThemeId(context)
      }
      return null
    }
  }
}